<script setup>
// Sticky banner for notifications awaiting this staff member's acknowledgement
// (#142, notifications-v2 only), on every page with the app header (BasePage).
// Shows the oldest; Acknowledge closes it for them here, and the next inbox
// refresh (app open, push arrival, back in the foreground) takes it off their
// other devices. Hidden on the inbox page of the notification it shows, which
// has its own button.
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { IconAlertCircle } from '@tabler/icons-vue'
import { useInboxStore } from '@/stores/inbox.js'

const inbox = useInboxStore()
const route = useRoute()
const d = __DICT__.inbox

const first = computed(() => inbox.pending[0] || null)
const more = computed(() => Math.max(0, inbox.pending.length - 1))
const visible = computed(() => {
  if (!inbox.enabled || !first.value) return false
  return !(route.name === 'Inbox' && Number(route.params?.notificationId) === first.value.id)
})
</script>

<template>
  <div v-if="visible" class="ack-banner alert alert-warning mb-3" role="status" aria-live="polite">
    <div class="d-flex align-items-start">
      <IconAlertCircle class="me-2 flex-shrink-0" :size="20" />
      <div class="flex-grow-1 min-w-0">
        <div class="fw-bold text-truncate">{{ first.title }}</div>
        <div class="ack-banner-body text-secondary">{{ first.body }}</div>
        <div class="d-flex flex-wrap align-items-center gap-2 mt-2">
          <button class="btn btn-sm btn-warning" :disabled="!!inbox.acknowledging" @click="inbox.acknowledge(first.id)">
            <span v-if="inbox.acknowledging === first.id" class="spinner-border spinner-border-sm me-1"></span>
            {{ d.acknowledge }}
          </button>
          <RouterLink :to="{ name: 'Inbox', params: { notificationId: first.id } }" class="btn btn-sm btn-ghost-secondary">
            {{ d.open }}
          </RouterLink>
          <RouterLink v-if="more" to="/inbox" class="small text-secondary">+{{ more }} {{ d.moreAwaiting }}</RouterLink>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.ack-banner {
  font-size: 0.875rem;
  position: sticky;
  /* Under the fixed app header */
  top: 3.5rem;
  z-index: 1020;
  background: var(--tblr-bg-surface);
  box-shadow: var(--tblr-box-shadow-sm);
}

.ack-banner-body {
  white-space: pre-line;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
